import { FC, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { CaretDownSvg, CaretRightSvg } from "../../../../../../../CommonComponents/svgs/Svgs";
import type { TestCaseConfig, TestCaseEntry } from "../../types/consoleTypes";
import { CriticalIcon } from "../../../../../../../Missions/Satellites/TestCase/svgs/TestCaseSvgs";

// ─── Field type inference ─────────────────────────────────────────────────────

type FieldType = "number" | "boolean" | "text";

/**
 * Infers input type from the original API string value.
 * - "true" / "false"          → boolean  (rendered as select)
 * - valid non-empty number    → number   (rendered as number input)
 * - everything else           → text
 */
function inferFieldType(rawValue: string): FieldType {
  const s = String(rawValue ?? "").trim();
  if (s === "true" || s === "false") return "boolean";
  if (s !== "" && !isNaN(Number(s))) return "number";
  return "text";
}

// ─── Single entry card ────────────────────────────────────────────────────────

interface TestCaseEntryCardProps {
  entry: TestCaseEntry;
  /** Stable per-entry key even when operationId is empty. */
  operationKey: string;
  index: number;
  /** Whether this card is currently expanded (controlled by parent). */
  expanded: boolean;
  /** Field values managed by the parent form state. */
  fieldValues: Record<string, string>;
  onFieldChange: (operationKey: string, fieldKey: string, value: string) => void;
  /** Toggle callback so parent can enforce "only one open" behavior. */
  onToggle: () => void;
}

const TestCaseEntryCard: FC<TestCaseEntryCardProps> = ({
  entry,
  operationKey,
  index,
  expanded,
  fieldValues,
  onFieldChange,
  onToggle,
}) => {

  const details = entry.testCaseEntryDetails ?? {};
  const fieldKeys = Object.keys(details);

  // Group by "parent path" so we can show compact labels and avoid overlap.
  // Example leaf key: "sequenceData.commandHeader.DaId"
  // - group header (no input): "sequenceData.commandHeader"
  // - field label (input): "DaId"
  const grouped = (() => {
    const out: Record<string, string[]> = {};
    fieldKeys.forEach((k) => {
      const lastDot = k.lastIndexOf(".");
      const group = lastDot > 0 ? k.slice(0, lastDot) : "";
      (out[group] ??= []).push(k);
    });
    // Stable rendering: root fields first, then grouped fields alphabetically
    const sortedGroups = Object.keys(out).sort((a, b) => {
      if (a === "" && b !== "") return -1;
      if (a !== "" && b === "") return 1;
      return a.localeCompare(b);
    });
    return sortedGroups.map((g) => ({
      group: g,
      keys: (out[g] ?? []).slice().sort((a, b) => a.localeCompare(b)),
    }));
  })();

  const labelForKey = (k: string) => {
    const lastDot = k.lastIndexOf(".");
    return lastDot >= 0 ? k.slice(lastDot + 1) : k;
  };

  const groupTitle = (g: string) => {
    const lastDot = g.lastIndexOf(".");
    return lastDot >= 0 ? g.slice(lastDot + 1) : g;
  };

  // Pair fields into rows of two for the 2-col grid
  const toPairs = (keys: string[]) => {
    const pairs: string[][] = [];
    for (let i = 0; i < keys.length; i += 2) pairs.push(keys.slice(i, i + 2));
    return pairs;
  };

  return (
    <div className="test__entry__card">
      {/* Header — always visible, toggles expand */}
      <button
        type="button"
        className="test__entry__card__header"
        onClick={onToggle}
      >
        <span className="test__entry__card__caret">
          {expanded ? <CaretDownSvg /> : <CaretRightSvg />}
        </span>
        <span className="test__entry__card__title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {entry.isCritical && (
            <span className="test__entry__card__critical-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <CriticalIcon />
            </span>
          )}
          <span>{entry.testCaseEntryType} {index + 1}</span>
          {entry.operationId && (
            <span className="test__entry__card__op-id"> · {entry.operationId}</span>
          )}
        </span>
      </button>

      {/* Expanded body — 2-column field grid */}
      {expanded && (
        <div className="test__entry__card__body">
          {grouped.map(({ group, keys }) => {
            const fieldPairs = toPairs(keys);
            return (
              <div key={group || "root"} className="test__entry__group">
                {group && (
                  <div className="test__entry__group__header">
                    <div className="test__entry__group__header__title">{groupTitle(group)}</div>
                  </div>
                )}
                {fieldPairs.map((pair, pairIdx) => (
                  <div key={`${group}:${pairIdx}`} className="test__entry__field__row">
                    {pair.map((key) => {
                      // Current displayed value — user override takes priority over API default
                      const currentValue =
                        fieldValues[`${operationKey}.${key}`] ??
                        (typeof details[key] === "object"
                          ? JSON.stringify(details[key] ?? "")
                          : String(details[key] ?? ""));

                      // Infer type from the ORIGINAL API default (not the live editable value)
                      const apiDefault =
                        typeof details[key] === "object"
                          ? JSON.stringify(details[key] ?? "")
                          : String(details[key] ?? "");
                      const fieldType = inferFieldType(apiDefault);
                      return (
                        <div key={key} className="test__entry__field__col">
                          <span className="input__label">{labelForKey(key)}</span>

                          {fieldType === "boolean" ? (
                            <select
                              className="test__entry__field__input test__entry__field__input--select"
                              value={currentValue}
                              onChange={(ev) => onFieldChange(operationKey, key, ev.target.value)}
                              onMouseDown={(ev) => ev.stopPropagation()}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : (
                            <input
                              type={fieldType}
                              className="test__entry__field__input"
                              value={currentValue}
                              onChange={(ev) => onFieldChange(operationKey, key, ev.target.value)}
                              onMouseDown={(ev) => ev.stopPropagation()}
                              {...(fieldType === "number" ? { step: 1, min: 0 } : {})}
                            />
                          )}
                        </div>
                      );
                    })}
                    {/* Fill empty second cell if odd number of fields */}
                    {pair.length === 1 && <div className="test__entry__field__col" />}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Show empty placeholder row if the entry has no fields */}
          {fieldKeys.length === 0 && (
            <div className="test__entry__field__row">
              <div className="test__entry__field__col">
                <span className="input__label">No fields defined</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── TestForm ─────────────────────────────────────────────────────────────────

interface TestFormProps {
  testCase: TestCaseConfig | null;
  /** Called with the current flat field-values map whenever any field changes. */
  onFormChange: (values: Record<string, string>, isValid: boolean) => void;
}

/**
 * Renders the accordion list of TC/TASK entries from the selected test case.
 * Mirrors DualEditorForm's role in BusForm — this is the "body" of the Test panel.
 * Field layout matches the Figma 2-column grid exactly.
 */
const TestForm: FC<TestFormProps> = ({ testCase, onFormChange }) => {
  // Flat map: `${operationId}.${fieldKey}` → typed value (overrides)
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // Index of the currently expanded card; -1 = all collapsed (single-expand mode).
  const [expandedIndex, setExpandedIndex] = useState<number>(-1);

  // Avoid accordion reset when parent re-renders with a new callback identity.
  const onFormChangeRef = useRef(onFormChange);
  useEffect(() => {
    onFormChangeRef.current = onFormChange;
  }, [onFormChange]);

  // Stable per-entry operation key: operationId if present else "entry-{idx}"
  const operationKeys = useMemo(() => {
    const entries = testCase?.testCaseDetails ?? [];
    return entries.map((entry, idx) => String(entry?.operationId ?? "").trim() || `entry-${idx + 1}`);
  }, [testCase?.testCaseId]);

  /**
   * Build base values from backend defaults.
   * NOTE: Some APIs return empty operationId for every entry; we must not
   * key the entire form off that, or entries will collide and validity will
   * never behave correctly.
   */
  const baseFieldValues = useMemo<Record<string, string>>(() => {
    if (!testCase) return {};
    const out: Record<string, string> = {};
    const entries = testCase.testCaseDetails ?? [];
    entries.forEach((entry, idx) => {
      const opKey = operationKeys[idx] ?? `entry-${idx + 1}`;
      const details = entry.testCaseEntryDetails ?? {};
      Object.keys(details).forEach((key) => {
        const compositeKey = `${opKey}.${key}`;
        const raw = details[key];
        out[compositeKey] =
          typeof raw === "object" ? JSON.stringify(raw ?? "") : String(raw ?? "");
      });
    });
    return out;
  }, [testCase?.testCaseId, operationKeys]);

  const computeIsValid = useCallback((_current: Record<string, string>): boolean => {
    const entries = testCase?.testCaseDetails ?? [];
    if (entries.length === 0) return false;
    // Match TT&C behavior: selecting a valid test case should enable execute
    // (contact + testCaseId are still enforced in FormSubmit).
    return true;
  }, [testCase?.testCaseId]);

  // Reset fields whenever the selected test case changes
  useEffect(() => {
    // Start from backend defaults; treat them as filled values.
    setFieldValues({});
    const isValid = computeIsValid({});
    onFormChangeRef.current({ ...baseFieldValues }, isValid);
    // Collapse all accordion cards whenever test case changes
    setExpandedIndex(-1);
  }, [testCase?.testCaseId, baseFieldValues, computeIsValid]);

  const handleFieldChange = useCallback(
    (operationKey: string, fieldKey: string, value: string) => {
      setFieldValues((prev) => {
        const next = { ...prev, [`${operationKey}.${fieldKey}`]: value };
        // Form is valid when every entry has all its fields effectively filled,
        // considering both backend defaults and any user overrides.
        const isValid = computeIsValid(next);
        // Merge base + overrides before sending upstream so Console can
        // submit the full, concrete payload.
        const mergedValues: Record<string, string> = {
          ...baseFieldValues,
          ...next,
        };
        onFormChangeRef.current(mergedValues, isValid);
        return next;
      });
    },
    [computeIsValid, baseFieldValues]
  );

  if (!testCase) return null;

  const { testCaseDetails = [] } = testCase;

  return (
    <div className="test__form__container">
      {testCaseDetails.map((entry, idx) => (
        <TestCaseEntryCard
          key={operationKeys[idx] ?? idx}
          entry={entry}
          operationKey={operationKeys[idx] ?? `entry-${idx + 1}`}
          index={idx}
          expanded={expandedIndex === idx}
          fieldValues={fieldValues}
          onFieldChange={handleFieldChange}
          onToggle={() => setExpandedIndex((current) => (current === idx ? -1 : idx))}
        />
      ))}
    </div>
  );
};

export default TestForm;
